import { useState } from 'react';
import { Button, Checkbox, Input, Select } from '../../components/ui';
import { visibleSections } from '../sections/types';
import { TASK_PRIORITY_LABELS } from '../tasks/types';
import { memberLabel } from './TaskTable';
import type { SelectOption } from '../../components/ui';
import type { SavedViewFilters, SavedViewSort } from '../savedViews/types';
import type { Section } from '../sections/types';
import type { TaskLookups } from './TaskTable';

/**
 * The List View toolbar (handoff 25): search, filters and sort over the
 * loaded task list. Its state is exactly a saved view's arrangement
 * (SavedViewFilters + SavedViewSort), so Stage 7 can save and restore it
 * without translation.
 *
 * Design pass 2: search and sort stay in the bar; everything else lives
 * behind one Filters button that opens a panel. The quiet default is a
 * single row instead of seven controls competing with the task list.
 *
 * Selects encode "no filter" as the empty string and translate to and from
 * the filter shape, where absence means unfiltered and null is a real value
 * (an unassigned task, say).
 */

const ANY = '';
const UNASSIGNED = '__unassigned__';
const UNSECTIONED = '__unsectioned__';

const DUE_OPTIONS: SelectOption[] = [
  { value: ANY, label: 'Any due date' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'tomorrow', label: 'Due tomorrow' },
  { value: 'this_week', label: 'Due this week' },
  { value: 'none', label: 'No due date' },
];

const COMPLETION_OPTIONS: SelectOption[] = [
  { value: ANY, label: 'Open and completed' },
  { value: 'open', label: 'Open only' },
  { value: 'completed', label: 'Completed only' },
];

const SORT_OPTIONS: SelectOption[] = [
  { value: 'position', label: 'Manual order' },
  { value: 'title', label: 'Title' },
  { value: 'due_date', label: 'Due date' },
  { value: 'priority', label: 'Priority' },
  { value: 'created_at', label: 'Date created' },
];

export function FilterToolbar({
  filters,
  sort,
  lookups,
  sections,
  matched,
  total,
  onFiltersChange,
  onSortChange,
}: {
  filters: SavedViewFilters;
  sort: SavedViewSort;
  lookups: TaskLookups;
  /** For the Section filter (handoff 18: sections are filters on the Board). */
  sections: Section[];
  matched: number;
  total: number;
  onFiltersChange: (next: SavedViewFilters) => void;
  onSortChange: (next: SavedViewSort) => void;
}) {
  const [open, setOpen] = useState(false);

  const active = Object.keys(filters).length > 0;
  // Search shows in the bar itself; the button counts what the panel holds.
  const panelCount = Object.keys(filters).filter((key) => key !== 'search').length;

  /** Set one filter field, deleting it entirely when it returns to "any". */
  function patch(change: Partial<SavedViewFilters>, cleared: boolean, key: keyof SavedViewFilters) {
    const next = { ...filters, ...change };
    if (cleared) {
      delete next[key];
    }
    onFiltersChange(next);
  }

  const assigneeOptions: SelectOption[] = [
    { value: ANY, label: 'Anyone' },
    { value: UNASSIGNED, label: 'Unassigned' },
    ...lookups.members.map((member) => ({ value: member.user_id, label: memberLabel(member) })),
  ];

  const statusOptions: SelectOption[] = [
    { value: ANY, label: 'Any status' },
    ...lookups.statuses
      .filter((status) => status.is_active)
      .map((status) => ({ value: status.id, label: status.name })),
  ];

  const sectionOptions: SelectOption[] = [
    { value: ANY, label: 'Any section' },
    { value: UNSECTIONED, label: 'No section' },
    ...visibleSections(sections).map((section) => ({ value: section.id, label: section.name })),
  ];

  const priorityOptions: SelectOption[] = [
    { value: ANY, label: 'Any priority' },
    { value: 'high', label: TASK_PRIORITY_LABELS.high },
    { value: 'medium', label: TASK_PRIORITY_LABELS.medium },
    { value: 'low', label: TASK_PRIORITY_LABELS.low },
  ];

  const assigneeValue =
    filters.assigneeId === undefined
      ? ANY
      : filters.assigneeId === null
        ? UNASSIGNED
        : filters.assigneeId;
  const sectionValue =
    filters.sectionId === undefined
      ? ANY
      : filters.sectionId === null
        ? UNSECTIONED
        : filters.sectionId;
  const completionValue =
    filters.isCompleted === undefined ? ANY : filters.isCompleted ? 'completed' : 'open';

  return (
    <div className="filter-toolbar">
      <div className="filter-toolbar-row">
        <div className="filter-toolbar-search">
          <Input
            label="Search tasks"
            labelHidden
            value={filters.search ?? ''}
            placeholder="Title, description or remarks"
            onChange={(event) =>
              patch({ search: event.target.value }, event.target.value === '', 'search')
            }
          />
        </div>
        <Button
          variant="secondary"
          aria-expanded={open}
          className={panelCount > 0 ? 'filter-toolbar-button--active' : undefined}
          onClick={() => setOpen((previous) => !previous)}
        >
          Filters{panelCount > 0 ? ` (${panelCount})` : ''}
        </Button>

        <div className="filter-toolbar-sort">
          <Select
            label="Sort by"
            labelHidden
            options={SORT_OPTIONS}
            value={sort.column ?? 'position'}
            onChange={(event) =>
              onSortChange(
                event.target.value === 'position'
                  ? {}
                  : {
                      column: event.target.value as NonNullable<SavedViewSort['column']>,
                      direction: sort.direction ?? 'asc',
                    },
              )
            }
          />
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Sort ${sort.direction === 'desc' ? 'descending' : 'ascending'} — click to flip`}
            onClick={() =>
              onSortChange({
                column: sort.column ?? 'position',
                direction: sort.direction === 'desc' ? 'asc' : 'desc',
              })
            }
          >
            {sort.direction === 'desc' ? '↓' : '↑'}
          </Button>
        </div>

        {active ? (
          <span className="filter-toolbar-summary">
            Showing {matched} of {total} {total === 1 ? 'task' : 'tasks'}
            <Button variant="ghost" size="sm" onClick={() => onFiltersChange({})}>
              Clear filters
            </Button>
          </span>
        ) : null}
      </div>

      {open && (
        <div className="filter-popover">
          <div className="filter-popover-grid">
            <Select
              label="Assignee"
              options={assigneeOptions}
              value={assigneeValue}
              onChange={(event) =>
                patch(
                  { assigneeId: event.target.value === UNASSIGNED ? null : event.target.value },
                  event.target.value === ANY,
                  'assigneeId',
                )
              }
            />
            <Select
              label="Status"
              options={statusOptions}
              value={filters.statusId ?? ANY}
              onChange={(event) =>
                patch({ statusId: event.target.value }, event.target.value === ANY, 'statusId')
              }
            />
            <Select
              label="Section"
              options={sectionOptions}
              value={sectionValue}
              onChange={(event) =>
                patch(
                  { sectionId: event.target.value === UNSECTIONED ? null : event.target.value },
                  event.target.value === ANY,
                  'sectionId',
                )
              }
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={filters.priority ?? ANY}
              onChange={(event) =>
                patch(
                  { priority: event.target.value as NonNullable<SavedViewFilters['priority']> },
                  event.target.value === ANY,
                  'priority',
                )
              }
            />
            <Select
              label="Due"
              options={DUE_OPTIONS}
              value={filters.dueWithin ?? ANY}
              onChange={(event) =>
                patch(
                  { dueWithin: event.target.value as NonNullable<SavedViewFilters['dueWithin']> },
                  event.target.value === ANY,
                  'dueWithin',
                )
              }
            />
            <Select
              label="Show"
              options={COMPLETION_OPTIONS}
              value={completionValue}
              onChange={(event) =>
                patch(
                  { isCompleted: event.target.value === 'completed' },
                  event.target.value === ANY,
                  'isCompleted',
                )
              }
            />
          </div>
          <div className="filter-popover-checks">
            <Checkbox
              label="Has comments"
              checked={filters.hasComments === true}
              onChange={(event) =>
                patch({ hasComments: true }, !event.target.checked, 'hasComments')
              }
            />
            <Checkbox
              label="Has subtasks"
              checked={filters.hasSubtasks === true}
              onChange={(event) =>
                patch({ hasSubtasks: true }, !event.target.checked, 'hasSubtasks')
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
